import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './lib/auth.js';
import { trpc } from './lib/trpc.js';
import { AgencyShell } from './layouts/AgencyShell.js';
import { PortalShell } from './layouts/PortalShell.js';
import { routes } from './routes.js';
import { PageLoading } from './components/ui/index.js';
import { CookieConsent } from './components/CookieConsent.js';
import {
  TermsAcceptanceModal,
  tosAccepted,
  markTosAccepted,
} from './components/TermsAcceptanceModal.js';
import { useLogDeliverable } from './contexts/LogDeliverableContext.js';
import { usePushSubscription } from './hooks/usePushSubscription.js';

// ─── Critical path — loaded eagerly (login + small public pages) ───────────
import { LoginScreen } from './screens/auth/LoginScreen.js';
import { SignUpScreen } from './screens/auth/SignUpScreen.js';
import { ForgotPassword } from './screens/auth/ForgotPassword.js';
import { ResetPassword } from './screens/auth/ResetPassword.js';
import { PrivacyPolicy } from './screens/legal/PrivacyPolicy.js';
import { Terms } from './screens/legal/Terms.js';
import { Landing } from './screens/marketing/Landing.js';

// ─── Agency screens — lazy loaded ──────────────────────────────────────────
// Agent F
const DashboardPlaceholder = lazy(() =>
  import('./screens/agency/_placeholders.js').then((m) => ({ default: m.DashboardPlaceholder })),
);
const ClientList = lazy(() =>
  import('./screens/agency/ClientList.js').then((m) => ({ default: m.ClientList })),
);
const ClientDetail = lazy(() =>
  import('./screens/agency/ClientDetail.js').then((m) => ({ default: m.ClientDetail })),
);
const ClientContactForm = lazy(() =>
  import('./screens/agency/ClientContactForm.js').then((m) => ({ default: m.ClientContactForm })),
);
const AgencySettings = lazy(() =>
  import('./screens/agency/AgencySettings.js').then((m) => ({ default: m.AgencySettings })),
);
const UserManagement = lazy(() =>
  import('./screens/agency/UserManagement.js').then((m) => ({ default: m.UserManagement })),
);

// Agent G
const EngagementList = lazy(() =>
  import('./screens/agency/EngagementList.js').then((m) => ({ default: m.EngagementList })),
);
const EngagementDetail = lazy(() =>
  import('./screens/agency/EngagementDetail.js').then((m) => ({ default: m.EngagementDetail })),
);
const ProjectList = lazy(() =>
  import('./screens/agency/ProjectList.js').then((m) => ({ default: m.ProjectList })),
);
const ProjectView = lazy(() =>
  import('./screens/agency/ProjectView.js').then((m) => ({ default: m.ProjectView })),
);
const ProjectProposals = lazy(() =>
  import('./screens/agency/ProjectProposals.js').then((m) => ({ default: m.ProjectProposals })),
);

// Agent H
const DeliverableList = lazy(() =>
  import('./screens/agency/DeliverableList.js').then((m) => ({ default: m.DeliverableList })),
);
const DeliverableDetail = lazy(() =>
  import('./screens/agency/DeliverableDetail.js').then((m) => ({ default: m.DeliverableDetail })),
);
const DocumentWorkspace = lazy(() =>
  import('./screens/agency/DocumentWorkspace.js').then((m) => ({ default: m.DocumentWorkspace })),
);
const ReportArchive = lazy(() =>
  import('./screens/agency/ReportArchive.js').then((m) => ({ default: m.ReportArchive })),
);
const ReportForm = lazy(() =>
  import('./screens/agency/ReportForm.js').then((m) => ({ default: m.ReportForm })),
);
const PromptInbox = lazy(() =>
  import('./screens/agency/PromptInbox.js').then((m) => ({ default: m.PromptInbox })),
);
const Invoices = lazy(() =>
  import('./screens/agency/Invoices.js').then((m) => ({ default: m.Invoices })),
);
const NotificationCentre = lazy(() =>
  import('./screens/agency/NotificationCentre.js').then((m) => ({
    default: m.NotificationCentre,
  })),
);
const AuditLog = lazy(() =>
  import('./screens/agency/AuditLog.js').then((m) => ({ default: m.AuditLog })),
);
const ProjectTemplates = lazy(() =>
  import('./screens/agency/ProjectTemplates.js').then((m) => ({ default: m.ProjectTemplates })),
);
const LogDeliverableModal = lazy(() =>
  import('./components/agency/LogDeliverableModal.js').then((m) => ({
    default: m.LogDeliverableModal,
  })),
);
const PromptOverlay = lazy(() =>
  import('./components/agency/PromptOverlay.js').then((m) => ({ default: m.PromptOverlay })),
);

// ─── Portal screens — lazy loaded ──────────────────────────────────────────
// Agent I
const InviteAccept = lazy(() =>
  import('./screens/portal/InviteAccept.js').then((m) => ({ default: m.InviteAccept })),
);
const PortalDashboard = lazy(() =>
  import('./screens/portal/PortalDashboard.js').then((m) => ({ default: m.PortalDashboard })),
);
const PortalProjectList = lazy(() =>
  import('./screens/portal/ProjectList.js').then((m) => ({ default: m.ProjectList })),
);
const PortalProjectView = lazy(() =>
  import('./screens/portal/ProjectView.js').then((m) => ({ default: m.ProjectView })),
);

// Agent J
const ApprovalQueue = lazy(() =>
  import('./screens/portal/ApprovalQueue.js').then((m) => ({ default: m.ApprovalQueue })),
);
const DeliverableApproval = lazy(() =>
  import('./screens/portal/DeliverableApproval.js').then((m) => ({
    default: m.DeliverableApproval,
  })),
);
const PortalReportArchive = lazy(() =>
  import('./screens/portal/PortalReportArchive.js').then((m) => ({
    default: m.PortalReportArchive,
  })),
);
const PortalReportViewer = lazy(() =>
  import('./screens/portal/PortalReportViewer.js').then((m) => ({
    default: m.PortalReportViewer,
  })),
);
const PortalDocumentWorkspace = lazy(() =>
  import('./screens/portal/PortalDocumentWorkspace.js').then((m) => ({
    default: m.PortalDocumentWorkspace,
  })),
);
const PortalInvoices = lazy(() =>
  import('./screens/portal/PortalInvoices.js').then((m) => ({ default: m.PortalInvoices })),
);
const ClientDocumentUpload = lazy(() =>
  import('./screens/portal/ClientDocumentUpload.js').then((m) => ({
    default: m.ClientDocumentUpload,
  })),
);
const ProposeProject = lazy(() =>
  import('./screens/portal/ProposeProject.js').then((m) => ({ default: m.ProposeProject })),
);

/**
 * Top-level routing. Auth-aware: anonymous → /login, agency → agency shell,
 * contact → /portal shell. Routes wrapped in Suspense for code-splitting.
 * Cookie consent banner sits outside Routes so it persists across pages.
 */
export function App() {
  return (
    <>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          {/* Public auth */}
          <Route path={routes.login} element={<LoginScreen />} />
          <Route path={routes.signUp} element={<SignUpScreen />} />
          <Route path={routes.portalLogin} element={<LoginScreen variant="portal" />} />
          <Route path={routes.forgotPassword} element={<ForgotPassword variant="agency" />} />
          <Route path={routes.resetPassword} element={<ResetPassword variant="agency" />} />
          <Route path={routes.portalForgotPassword} element={<ForgotPassword variant="portal" />} />
          <Route path={routes.portalResetPassword} element={<ResetPassword variant="portal" />} />

          {/* Legal — public, no auth required */}
          <Route path={routes.privacy} element={<PrivacyPolicy />} />
          <Route path={routes.terms} element={<Terms />} />

          {/* Portal public flows (outside auth boundary) */}
          <Route path="/portal/invite/:token" element={<InviteAccept />} />

          {/* Agency app */}
          <Route element={<RequireAgencyOrRedirect />}>
            <Route element={<AgencyShell />}>
              {/* Agent F */}
              <Route path={routes.dashboard} element={<DashboardPlaceholder />} />
              <Route path={routes.clients} element={<ClientList />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/clients/:clientId/contacts/new" element={<ContactNewPage />} />
              <Route
                path="/clients/:clientId/contacts/:contactId/edit"
                element={<ContactEditPage />}
              />
              <Route path={routes.agencySettings} element={<AgencySettings />} />
              <Route path={routes.users} element={<UserManagement />} />
              {/* Agent G */}
              <Route path={routes.engagements} element={<EngagementList />} />
              <Route path="/engagements/:id" element={<EngagementDetail />} />
              <Route path={routes.projects} element={<ProjectList />} />
              <Route path={routes.projectProposals} element={<ProjectProposals />} />
              <Route path="/projects/:id" element={<ProjectView />} />
              {/* Agent H */}
              <Route path={routes.deliverables} element={<DeliverableList />} />
              <Route path={routes.deliverable(':id')} element={<DeliverableDetail />} />
              <Route path={routes.documents} element={<DocumentWorkspace />} />
              <Route path={routes.reports} element={<ReportArchive />} />
              <Route path={routes.reportNew} element={<ReportForm />} />
              <Route path={routes.reportEdit(':id')} element={<ReportForm />} />
              <Route path={routes.inbox} element={<PromptInbox />} />
              <Route path={routes.invoices} element={<Invoices />} />
              <Route path={routes.notifications} element={<NotificationCentre />} />
              <Route path={routes.auditLog} element={<AuditLog />} />
              <Route path={routes.projectTemplates} element={<ProjectTemplates />} />
            </Route>
          </Route>

          {/* Client portal */}
          <Route path="/portal" element={<RequireContactOrRedirect />}>
            <Route element={<PortalShell />}>
              {/* Agent I */}
              <Route index element={<PortalDashboard />} />
              <Route path="projects" element={<PortalProjectList />} />
              <Route path="projects/:id" element={<PortalProjectView />} />
              {/* Agent J */}
              <Route path="approvals" element={<ApprovalQueue />} />
              <Route path="approvals/:id" element={<DeliverableApproval />} />
              <Route path="reports" element={<PortalReportArchive />} />
              <Route path="reports/:id" element={<PortalReportViewer />} />
              <Route path="documents" element={<PortalDocumentWorkspace />} />
              <Route path="invoices" element={<PortalInvoices />} />
              <Route path="documents/upload" element={<ClientDocumentUpload />} />
              <Route path="propose-project" element={<ProposeProject />} />
              <Route path="notifications" element={<NotificationCentre />} />
            </Route>
          </Route>

          {/* Public marketing landing at /. Authed users skip past it to
              their dashboard via RootEntry — they never see marketing. */}
          <Route path="/" element={<RootEntry />} />

          {/* Default redirect for any other unmatched path */}
          <Route path="*" element={<AuthAwareRedirect />} />
        </Routes>
      </Suspense>

      {/* Global — outside Routes so it persists across all navigations */}
      <CookieConsent />
    </>
  );
}

function ContactNewPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  if (!clientId) return null;
  return (
    <div className="min-h-screen bg-canvas px-4 py-6 md:px-10 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-scope-navy">Add contact</h1>
      </div>
      <div className="max-w-lg rounded-xl border border-scope-border-soft bg-white p-8">
        <ClientContactForm
          clientId={clientId}
          onSuccess={() => navigate(`/clients/${clientId}`)}
          onCancel={() => navigate(`/clients/${clientId}`)}
        />
      </div>
    </div>
  );
}

function ContactEditPage() {
  const { clientId, contactId } = useParams<{ clientId: string; contactId: string }>();
  const navigate = useNavigate();
  if (!clientId || !contactId) return null;
  return (
    <div className="min-h-screen bg-canvas px-4 py-6 md:px-10 md:py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-scope-navy">Edit contact</h1>
      </div>
      <div className="max-w-lg rounded-xl border border-scope-border-soft bg-white p-8">
        <ClientContactForm
          clientId={clientId}
          contactId={contactId}
          onSuccess={() => navigate(`/clients/${clientId}`)}
          onCancel={() => navigate(`/clients/${clientId}`)}
        />
      </div>
    </div>
  );
}

/** Mounted alongside the agency shell — hosts modals and overlays triggered
 * from anywhere within the agency app (Log Deliverable, Prompt overlay). */
function AgencyGlobalComponents() {
  const { isOpen, close } = useLogDeliverable();
  return (
    <>
      <PromptOverlay />
      <LogDeliverableModal open={isOpen} onClose={close} />
    </>
  );
}

/** Wraps authenticated routes to show the ToS modal on first login. */
function RequireToS({ children }: { children: ReactNode }) {
  const { auth } = useAuth();
  const [localAccepted, setLocalAccepted] = useState(() => tosAccepted());

  // Server is the source of truth — termsAcceptedAt persists across devices,
  // unlike the localStorage fast-path which is per-device (so a user who
  // accepted on one device was re-prompted on the next).
  const userMeQ = trpc.user.me.useQuery(undefined, { enabled: auth.kind === 'agency' });
  const contactMeQ = trpc.clientContact.me.useQuery(undefined, {
    enabled: auth.kind === 'contact',
  });
  const serverAccepted =
    (auth.kind === 'agency' && userMeQ.data?.termsAcceptedAt != null) ||
    (auth.kind === 'contact' && contactMeQ.data?.termsAcceptedAt != null);

  // Once the server confirms acceptance, cache it locally so future loads
  // skip the round-trip.
  useEffect(() => {
    if (serverAccepted && !localAccepted) {
      markTosAccepted();
      setLocalAccepted(true);
    }
  }, [serverAccepted, localAccepted]);

  if (auth.kind === 'anonymous') return <>{children}</>;
  if (localAccepted || serverAccepted) return <>{children}</>;
  // Don't prompt until we know the server's answer — avoids flashing the modal
  // at an already-accepted user on a fresh device.
  if (auth.kind === 'agency' ? userMeQ.isLoading : contactMeQ.isLoading) return <>{children}</>;

  return (
    <>
      <div className="pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      <TermsAcceptanceModal onAccepted={() => setLocalAccepted(true)} />
    </>
  );
}

function RequireAgencyOrRedirect() {
  const { auth, loading, recovery } = useAuth();
  usePushSubscription(auth);
  if (loading) return <PageLoading />;
  if (recovery) {
    // Password-recovery sessions must NOT grant full app access — send
    // them to the reset screen until they actually set a new password.
    return (
      <Navigate
        to={auth.kind === 'contact' ? routes.portalResetPassword : routes.resetPassword}
        replace
      />
    );
  }
  if (auth.kind === 'agency')
    return (
      <RequireToS>
        <>
          <AgencyGlobalComponents />
          <Outlet />
        </>
      </RequireToS>
    );
  if (auth.kind === 'contact') return <Navigate to={routes.portalDashboard} replace />;
  return <Navigate to={routes.login} replace />;
}

function RequireContactOrRedirect() {
  const { auth, loading, recovery } = useAuth();
  usePushSubscription(auth);
  if (loading) return <PageLoading />;
  if (recovery) {
    return (
      <Navigate
        to={auth.kind === 'agency' ? routes.resetPassword : routes.portalResetPassword}
        replace
      />
    );
  }
  if (auth.kind === 'contact')
    return (
      <RequireToS>
        <Outlet />
      </RequireToS>
    );
  if (auth.kind === 'agency') return <Navigate to={routes.dashboard} replace />;
  return <Navigate to={routes.portalLogin} replace />;
}

function AuthAwareRedirect() {
  const { auth, loading, recovery } = useAuth();
  if (loading) return <PageLoading />;
  if (recovery) {
    return (
      <Navigate
        to={auth.kind === 'contact' ? routes.portalResetPassword : routes.resetPassword}
        replace
      />
    );
  }
  if (auth.kind === 'agency') return <Navigate to={routes.dashboard} replace />;
  if (auth.kind === 'contact') return <Navigate to={routes.portalDashboard} replace />;
  return <Navigate to={routes.login} replace />;
}

/**
 * Root path handler. Anonymous visitors get the public marketing landing;
 * authed users are sent straight to their own home so the marketing page
 * never gets in their way.
 */
function RootEntry() {
  const { auth, loading, recovery } = useAuth();
  if (loading) return <PageLoading />;
  if (recovery) {
    return (
      <Navigate
        to={auth.kind === 'contact' ? routes.portalResetPassword : routes.resetPassword}
        replace
      />
    );
  }
  if (auth.kind === 'agency') return <Navigate to={routes.dashboard} replace />;
  if (auth.kind === 'contact') return <Navigate to={routes.portalDashboard} replace />;
  return <Landing />;
}
